import {
    InsightType,
    type Status,
    type Step,
} from "@amzn/d16-g-client-deck-builder-typescript-client";
import { Flex, Tag } from "@amzn/storm-ui";
import React from "react";
import { isNonNullish } from "remeda";

import { useContentCardShown } from "../../../../../hooks/misc/useContentCardShown";
import { CardDetailsSource, type CardDetail } from "../../../../../types/card";
import { formatDateTime } from "../../../../../utils/date.utils";
import { getCardProgress } from "../../../../../utils/insightCard.utils";
import { getAdvertiserId } from "../../../../../utils/permission.utils";
import { textToVariant } from "../../../../../utils/storm.utils";
import { CardLayout } from "../../insightDetails/CardLayout";
import { CardLayoutV2 } from "../../insightDetails/CardLayoutV2";
import type { GeneratedInsightCardProps } from "../GeneratedInsightCard";

import { CuratedInsightCardAction } from "./CuratedInsightCardAction";

export const CuratedInsightCard: React.FunctionComponent<
    GeneratedInsightCardProps
> = ({ insight }) => {
    const {
        identifier: executionId,
        title,
        step,
        status,
        metadata,
        updateTimestamp,
    } = insight;
    const advertiserId = getAdvertiserId();
    const details: CardDetail[] = [];
    const description = isNonNullish(metadata)
        ? metadata["description"]
        : undefined;

    const outputFileFormats = isNonNullish(metadata)
        ? metadata["outputFileFormats"] &&
          JSON.parse(metadata["outputFileFormats"])
        : ["pptx"];

    useContentCardShown({
        insightType: InsightType.WB,
        identifier: executionId,
    });

    const tags: string[] = [];
    if (
        metadata &&
        metadata["sourceSummaries"] &&
        Array.isArray(metadata["sourceSummaries"])
    ) {
        tags.push(...metadata["sourceSummaries"]);
    }

    if (metadata && metadata["description"]) {
        details.push({
            label: "Description",
            value: metadata["description"],
            renderOn: [CardDetailsSource.SIDEPANEL],
        });
    }

    if (metadata && metadata["globalWorkbench"]) {
        return (
            <CardLayoutV2
                header="Workbench"
                description={description}
                footer={
                    updateTimestamp
                        ? `Created on: ${formatDateTime(updateTimestamp)}`
                        : "Creation date unavailable"
                }
                title={title}
                details={details}
                dataTestId={executionId ?? ""}
                postDescription={
                    <Flex style={{ gap: "0.2rem" }}>
                        {tags.map((tag) => (
                            <Tag
                                key={tag}
                                label={tag}
                                variant={textToVariant(tag)}
                            />
                        ))}
                    </Flex>
                }
                actions={
                    <CuratedInsightCardAction
                        title={title!}
                        details={details}
                        executionId={executionId!}
                        advertiserId={advertiserId!}
                        progress={getCardProgress(
                            step as Step,
                            status as Status,
                        )}
                        description={description}
                        outputFileFormats={outputFileFormats}
                        insight={insight}
                    />
                }
            />
        );
    }
    return (
        <CardLayout
            key={executionId}
            dataTestId={executionId ?? ""}
            description={title ?? ""}
            details={details}
            actions={
                <CuratedInsightCardAction
                    title={title!}
                    details={details}
                    executionId={executionId!}
                    advertiserId={advertiserId!}
                    progress={getCardProgress(step as Step, status as Status)}
                    description={description}
                    outputFileFormats={outputFileFormats}
                    insight={insight}
                />
            }
        />
    );
};
